import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { sendPasswordResetEmail } from "firebase/auth"

import { auth } from "@/config/firebase"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import { useAuth } from "@/features/auth/context/AuthContext"

export interface AppBarState {
  mobileOpen: boolean
  isClosing: boolean
  handleDrawerClose: () => void
  handleDrawerTransitionEnd: () => void
  handleDrawerToggle: () => void
  logoutConfirmOpen: boolean
  closeLogoutConfirm: () => void
  confirmLogout: () => void
  onLogout: () => void
  passwordResetConfirmOpen: boolean
  closePasswordResetConfirm: () => void
  confirmPasswordReset: () => Promise<void>
  handlePasswordReset: () => void
}

const useAppBarState = (): AppBarState => {
  const { user } = useAuth()
  const { showSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const [mobileOpen, setMobileOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const [passwordResetConfirmOpen, setPasswordResetConfirmOpen] = useState(false)

  const handleDrawerClose = useCallback(() => {
    setIsClosing(true)
    setMobileOpen(false)
  }, [])

  const handleDrawerTransitionEnd = useCallback(() => {
    setIsClosing(false)
  }, [])

  const handleDrawerToggle = useCallback(() => {
    if (!isClosing) setMobileOpen(old => !old)
  }, [isClosing])

  const onLogout = useCallback(() => {
    setLogoutConfirmOpen(true)
  }, [])

  const closeLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(false)
  }, [])

  const confirmLogout = useCallback(() => {
    setLogoutConfirmOpen(false)
    auth.signOut().then(() => {
      navigate("/")
    })
  }, [navigate])

  const handlePasswordReset = useCallback(() => {
    setPasswordResetConfirmOpen(true)
  }, [])

  const closePasswordResetConfirm = useCallback(() => {
    setPasswordResetConfirmOpen(false)
  }, [])

  const confirmPasswordReset = useCallback(async () => {
    setPasswordResetConfirmOpen(false)

    try {
      if (user?.email) {
        await sendPasswordResetEmail(auth, user.email)
        showSnackbar("Password reset email sent!", "info")
      }
    } catch (error) {
      showSnackbar("Failed to send reset email", "error")
    }
  }, [showSnackbar, user])

  return {
    mobileOpen,
    isClosing,
    handleDrawerClose,
    handleDrawerTransitionEnd,
    handleDrawerToggle,
    logoutConfirmOpen,
    closeLogoutConfirm,
    confirmLogout,
    onLogout,
    passwordResetConfirmOpen,
    closePasswordResetConfirm,
    confirmPasswordReset,
    handlePasswordReset
  }
}

export default useAppBarState
